// FHIR PAS (Prior Authorization Support) Connector
// Implements Da Vinci PAS IG: https://build.fhir.org/ig/HL7/davinci-pas/

import { AuthorizationRequest } from '@/lib/authorization/types';
import { SubmissionDetails } from './types';

export interface PASRequestPayload {
  resourceType: 'Bundle';
  type: 'collection';
  entry: PASEntry[];
}

interface PASEntry {
  resource: {
    resourceType: string;
    [key: string]: unknown;
  };
}

export interface PASResponsePayload {
  resourceType: 'ClaimResponse';
  status: string;
  outcome: 'complete' | 'error' | 'partial' | 'queued';
  preAuthRef?: string;
  preAuthPeriod?: {
    start?: string;
    end?: string;
  };
  disposition?: string;
  error?: Array<{
    code: {
      coding?: Array<{ code: string; display: string }>;
      text?: string;
    };
  }>;
  item?: Array<{
    itemSequence: number;
    adjudication?: Array<{
      category: { coding: Array<{ code: string }> };
      reason?: { coding: Array<{ code: string; display: string }> };
    }>;
  }>;
}

/**
 * Build a PAS-compliant request payload from case data
 */
export function buildPASRequestPayload(
  caseData: AuthorizationRequest,
  patient?: { mrn: string; first_name: string; last_name: string; dob: string },
  payer?: { name: string; payer_id: string }
): PASRequestPayload {
  const entries: PASEntry[] = [];
  
  // Patient resource
  if (patient) {
    entries.push({
      resource: {
        resourceType: 'Patient',
        identifier: [
          {
            type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v2-0203', code: 'MR' }] },
            value: patient.mrn,
          },
        ],
        name: [
          {
            family: patient.last_name,
            given: [patient.first_name],
          },
        ],
        birthDate: patient.dob,
      },
    });
  }
  
  // Coverage resource
  if (payer) {
    entries.push({
      resource: {
        resourceType: 'Coverage',
        status: 'active',
        payor: [
          {
            identifier: { value: payer.payer_id },
            display: payer.name,
          },
        ],
      },
    });
  }
  
  // Claim resource (the actual prior auth request)
  const claimResource: Record<string, unknown> = {
    resourceType: 'Claim',
    status: 'active',
    type: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/claim-type',
          code: 'professional',
        },
      ],
    },
    use: 'preauthorization',
    priority: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/processpriority',
          code: caseData.urgency === 'emergent' ? 'stat' : caseData.urgency === 'urgent' ? 'urgent' : 'normal',
        },
      ],
    },
    diagnosis: caseData.icd10_codes.map((code, index) => ({
      sequence: index + 1,
      diagnosisCodeableConcept: {
        coding: [
          {
            system: 'http://hl7.org/fhir/sid/icd-10-cm',
            code: code,
          },
        ],
      },
    })),
    item: caseData.cpt_codes.map((code, index) => {
      const item: Record<string, unknown> = {
        sequence: index + 1,
        productOrService: {
          coding: [
            {
              system: 'http://www.ama-assn.org/go/cpt',
              code: code,
            },
          ],
        },
        diagnosisSequence: caseData.icd10_codes.map((_, i) => i + 1),
      };
      
      // Add modifiers if present
      if (caseData.modifiers && caseData.modifiers.length > 0) {
        item.modifier = caseData.modifiers.map(mod => ({
          coding: [
            {
              system: 'https://www.cms.gov/Medicare/Coding/HCPCSReleaseCodeSets',
              code: mod,
            },
          ],
        }));
      }
      
      // Add scheduled date if present
      if (caseData.scheduled_date) {
        item.servicedDate = caseData.scheduled_date;
      }
      
      return item;
    }),
  };
  
  entries.push({ resource: claimResource as PASEntry['resource'] });
  
  return {
    resourceType: 'Bundle',
    type: 'collection',
    entry: entries,
  };
}

/**
 * Parse PAS response into normalized status
 */
export function parsePASResponse(response: PASResponsePayload): {
  normalized_status: 'APPROVED' | 'DENIED' | 'IN_REVIEW' | 'NEED_MORE_INFO';
  auth_number?: string;
  effective_start?: string;
  effective_end?: string;
  denial_reason?: string;
  pending_reason?: string;
} {
  const result: ReturnType<typeof parsePASResponse> = {
    normalized_status: 'IN_REVIEW',
  };
  
  switch (response.outcome) {
    case 'complete':
      if (response.preAuthRef) {
        result.normalized_status = 'APPROVED';
        result.auth_number = response.preAuthRef;
        if (response.preAuthPeriod) {
          result.effective_start = response.preAuthPeriod.start;
          result.effective_end = response.preAuthPeriod.end;
        }
      } else {
        result.normalized_status = 'DENIED';
        result.denial_reason = response.disposition || 'Authorization denied';
      }
      break;
    case 'partial':
      result.normalized_status = 'NEED_MORE_INFO';
      result.pending_reason = response.disposition || 'Additional information required';
      break;
    case 'error':
      result.normalized_status = 'DENIED';
      if (response.error && response.error.length > 0) {
        result.denial_reason = response.error
          .map(e => e.code.text || e.code.coding?.[0]?.display)
          .filter(Boolean)
          .join('; ');
      }
      break;
    case 'queued':
    default:
      result.normalized_status = 'IN_REVIEW';
      result.pending_reason = response.disposition || 'Request queued for review';
  }
  
  return result;
}

/**
 * Submit PAS request to payer endpoint
 */
export async function submitPASRequest(
  endpoint: string,
  payload: PASRequestPayload,
  accessToken?: string
): Promise<{ success: boolean; response?: PASResponsePayload; error?: string }> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/fhir+json',
      'Accept': 'application/fhir+json',
    };
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const response = await fetch(`${endpoint}/Claim/$submit`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `PAS submission failed: ${response.status} - ${errorText}`,
      };
    }
    
    const data = await response.json();
    return {
      success: true,
      response: data as PASResponsePayload,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during PAS submission',
    };
  }
}

/**
 * Check if PAS is available for a given payer configuration
 */
export function isPASAvailable(config?: { fhir_pas_enabled?: boolean; fhir_pas_endpoint?: string }): boolean {
  return !!(config?.fhir_pas_enabled && config?.fhir_pas_endpoint);
}

/**
 * Generate submission details for PAS lane
 */
export function generatePASSubmissionDetails(
  endpoint: string,
  requestId: string
): SubmissionDetails {
  return {
    pas_endpoint: endpoint,
    pas_request_id: requestId,
  };
}
